import type { ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { FullPageLoading } from "@/components/layout/FullPageLoading";
import { useSession } from "@/lib/session/SessionContext";
import { LoginPage } from "@/routes/LoginPage";
import { RegisterPage } from "@/routes/RegisterPage";
import { ForgotPasswordPage } from "@/routes/ForgotPasswordPage";
import { ResetPasswordPage } from "@/routes/ResetPasswordPage";
import { ChangePasswordPage } from "@/routes/ChangePasswordPage";
import { AppHomePage } from "@/routes/AppHomePage";
import { WizardUIProvider } from "@/features/onboarding/WizardUIContext";
import { usesMobileApp } from "@shared/roles.js";
import { homeFor } from "@/lib/session/homeRoute";

function RootRedirect() {
  const { status, session } = useSession();
  if (status === "loading") return <FullPageLoading />;
  if (!session) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(session)} replace />;
}

/** Login/Register/Forgot-Password: bei bestehender Session zur passenden Startseite. */
function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status, session } = useSession();
  if (status === "loading") return <FullPageLoading />;
  if (session) return <Navigate to={homeFor(session)} replace />;
  return <>{children}</>;
}

/**
 * /change-password: braucht eine Sitzung, aber NICHT das mustChangePassword-Gate
 * selbst. Seit S1.6 auch für Mitglieder — ihr Erstpasswort stammt aus dem
 * Onboarding-Wizard und wird auf Papier übergeben, sie müssen also durch
 * dieselbe Tür.
 */
function RequirePasswordChangeSession() {
  const { status, session } = useSession();
  if (status === "loading") return <FullPageLoading />;
  if (!session || (session.actorType !== "admin" && session.actorType !== "member")) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RequireAdmin() {
  const { status, session } = useSession();
  if (status === "loading") return <FullPageLoading />;
  if (!session || session.actorType !== "admin") return <Navigate to="/login" replace />;
  if (session.user.mustChangePassword) return <Navigate to="/change-password" replace />;
  return <Outlet />;
}

/** Mitglied eines Mandanten — die eigentliche cleverfleet-App. Analog zu RequireAdmin. */
function RequireMember() {
  const { status, session } = useSession();
  if (status === "loading") return <FullPageLoading />;
  if (!session || session.actorType !== "member") return <Navigate to="/login" replace />;
  // Dieselbe Schranke wie bei RequireAdmin (S1.6): wer noch mit dem Passwort
  // aus dem Kundenreport unterwegs ist, ersetzt es zuerst.
  if (session.mustChangePassword) return <Navigate to="/change-password" replace />;
  return <Outlet />;
}

/**
 * Die Fahrer-App (S4.1) — nur für die drei Rollen mit Mobile-Lizenz
 * (shared/roles.js). Läuft innerhalb von RequireMember, prüft also nur noch die
 * Rolle; wer sie nicht trägt, landet auf der Mandanten-Startseite statt am
 * Login. Eine Schranke ist das nicht: was jemand an einem Auftrag tun darf,
 * entscheidet weiterhin der Server (server/orders/transitions.js).
 */
function RequireMobileRole() {
  const { session } = useSession();
  // Ladezustand, fehlende Sitzung und falscher Akteurstyp sind hier bereits
  // erledigt — das Element sitzt als Kind von RequireMember. Sie noch einmal zu
  // prüfen las sich, als wäre /app/heute auch ohne Anmeldung erreichbar
  // (Gatter G4).
  if (session?.actorType === "member" && !usesMobileApp(session.role)) return <Navigate to="/app" replace />;
  return <Outlet />;
}

/** Kunden-Session ODER impersonierender Admin (serverseitig bereits eine Kunden-Session). */
function RequireCustomer() {
  const { status, session } = useSession();
  if (status === "loading") return <FullPageLoading />;
  if (!session) return <Navigate to="/login" replace />;
  if (session.actorType !== "customer") return <Navigate to="/admin" replace />;
  return (
    <WizardUIProvider>
      <Outlet />
    </WizardUIProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { index: true, element: <RootRedirect /> },
      {
        element: (
          <RedirectIfAuthenticated>
            <Outlet />
          </RedirectIfAuthenticated>
        ),
        children: [
          { path: "login", element: <LoginPage /> },
          { path: "register", element: <RegisterPage /> },
          { path: "forgot-password", element: <ForgotPasswordPage /> }
        ]
      },
      { path: "reset-password", element: <ResetPasswordPage /> },
      {
        element: <RequirePasswordChangeSession />,
        children: [{ path: "change-password", element: <ChangePasswordPage /> }]
      },
      {
        // Admin-Dashboard und Onboarding-Wizard sind pro Session immer exklusiv
        // (ein Kunde sieht nie das Admin-Feature und umgekehrt), deshalb lädt
        // jede Route erst beim ersten Besuch als eigener Chunk nach, statt
        // beide vollständigen Feature-Bäume in den Haupt-Bundle zu ziehen.
        element: <RequireAdmin />,
        children: [
          {
            path: "admin",
            lazy: async () => {
              const { AdminPage } = await import("@/routes/AdminPage");
              return { Component: AdminPage };
            }
          },
          {
            path: "admin/analyse",
            lazy: async () => {
              const { AnalysePage } = await import("@/routes/AnalysePage");
              return { Component: AnalysePage };
            }
          },
          {
            path: "admin/notifications",
            lazy: async () => {
              const { NotificationsPage } = await import("@/routes/NotificationsPage");
              return { Component: NotificationsPage };
            }
          },
          {
            path: "admin/settings",
            lazy: async () => {
              const { AdminSettingsPage } = await import("@/routes/AdminSettingsPage");
              return { Component: AdminSettingsPage };
            }
          },
          { path: "admin/settings/:tab", element: <Navigate to="/admin/settings" replace /> }
        ]
      },
      {
        // Die eigentliche cleverfleet-App wächst hier (src/features/tenant/).
        // /app bleibt vorerst die dünne Platzhalter-Startseite; die
        // Stammdatenpflege (S1.3) lädt als eigener Chunk nach.
        element: <RequireMember />,
        children: [
          { path: "app", element: <AppHomePage /> },
          {
            path: "app/stammdaten/:submodule",
            lazy: async () => {
              const { TenantSubmodulePage } = await import("@/routes/TenantSubmodulePage");
              return { Component: TenantSubmodulePage };
            }
          },
          {
            path: "app/auftraege",
            lazy: async () => {
              const { OrdersPage } = await import("@/routes/OrdersPage");
              return { Component: OrdersPage };
            }
          },
          {
            path: "app/dispo",
            lazy: async () => {
              const { DispoPage } = await import("@/routes/DispoPage");
              return { Component: DispoPage };
            }
          },
          {
            element: <RequireMobileRole />,
            children: [
              {
                path: "app/heute",
                lazy: async () => {
                  const { MobileDayPage } = await import("@/routes/MobileDayPage");
                  return { Component: MobileDayPage };
                }
              },
              {
                path: "app/heute/:orderId",
                lazy: async () => {
                  const { MobileOrderPage } = await import("@/routes/MobileOrderPage");
                  return { Component: MobileOrderPage };
                }
              }
            ]
          }
        ]
      },
      {
        element: <RequireCustomer />,
        children: [
          {
            path: "onboarding/stammdaten/:submodule",
            lazy: async () => {
              const { OnboardingSubmodulePage } = await import("@/routes/OnboardingSubmodulePage");
              return { Component: OnboardingSubmodulePage };
            }
          },
          {
            path: "onboarding/:stepId",
            lazy: async () => {
              const { OnboardingStepPage } = await import("@/routes/OnboardingStepPage");
              return { Component: OnboardingStepPage };
            }
          }
        ]
      },
      { path: "*", element: <Navigate to="/" replace /> }
    ]
  }
]);
